<template>
  <div class="login">
    <div class="login__card">
      <div class="login__brand">
        <SigdexMark class="login__mark" />
        <span class="login__wordmark-name">Sigdex Studio</span>
      </div>

      <form class="login__form" @submit.prevent="handleLogin">
        <StField label="Email or username">
          <StTextInput v-model="emailOrUsername" autocomplete="username" :disabled="busy" />
        </StField>

        <StField label="Password">
          <StTextInput
            v-model="password"
            type="password"
            autocomplete="current-password"
            :disabled="busy"
          />
        </StField>

        <StButton type="submit" variant="primary" block :loading="busy && mode === 'password'">
          Sign in
        </StButton>
      </form>

      <div class="login__divider"><span>or</span></div>

      <StButton
        class="login__discord"
        block
        :disabled="busy"
        :loading="busy && mode === 'discord'"
        @click="handleDiscord"
      >
        <template #icon>
          <font-awesome-icon :icon="['fab', 'discord']" />
        </template>
        Continue with Discord
      </StButton>

      <p v-if="error" class="login__error">{{ error }}</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import SigdexMark from './SigdexMark.vue';
import { StField, StTextInput, StButton } from './ui/index.js';
import { client, parseErrorMessage } from '../client.js';

const emailOrUsername = ref('');
const password = ref('');
const error = ref('');
const busy = ref(false);
const mode = ref<'password' | 'discord' | null>(null);

async function handleLogin() {
  error.value = '';
  busy.value = true;
  mode.value = 'password';
  try {
    await client.login(emailOrUsername.value, password.value);
    // App.vue reacts to the auth state change; nothing more to do here.
  } catch (err: any) {
    error.value = parseErrorMessage(err);
  } finally {
    busy.value = false;
    mode.value = null;
  }
}

async function handleDiscord() {
  error.value = '';
  busy.value = true;
  mode.value = 'discord';
  try {
    // Redirects the browser to Discord; control returns via the callback.
    await client.startDiscordLogin();
  } catch (err: any) {
    error.value = parseErrorMessage(err);
    busy.value = false;
    mode.value = null;
  }
}
</script>

<style scoped>
.login {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--st-space-5);
  background: var(--st-shell-bg);
}
.login__card {
  width: 100%;
  max-width: 380px;
  padding: var(--st-space-6);
  background: var(--st-surface);
  border: 1px solid var(--st-border);
  border-radius: var(--st-radius-lg);
  box-shadow: var(--st-shadow-3);
}
.login__brand {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--st-space-4);
  margin-bottom: var(--st-space-6);
}
.login__mark {
  height: 5rem;
  color: var(--st-accent);
}
.login__wordmark-name {
  font-family: var(--st-font-display);
  font-size: var(--st-text-xl);
  font-weight: var(--st-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--st-text);
}
.login__form {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-4);
}
.login__divider {
  display: flex;
  align-items: center;
  gap: var(--st-space-3);
  margin: var(--st-space-4) 0;
  color: var(--st-text-subtle);
  font-size: var(--st-text-xs);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.login__divider::before,
.login__divider::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--st-border);
}
.login__error {
  margin-top: var(--st-space-4);
  padding: var(--st-space-2) var(--st-space-3);
  background: var(--st-danger-tint, rgba(220, 38, 38, 0.1));
  color: var(--st-danger, #dc2626);
  border-radius: var(--st-radius-sm);
  font-size: var(--st-text-sm);
  text-align: center;
}
</style>
